import React, { useState } from 'react'
import {
  Alert,
  Box,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import moment from 'moment'
import API from '../../../../services/API'
import StaffSelector from '../../../events/StaffSelector'
import Attachments from '../../../../components/Attachments/Attachments'
import FeedbackDrawer from '../../feedback/FeedbackDrawer'
import AnDateField from '../../../../components/DateTime/DateField'
import AnTimeField from '../../../../components/DateTime/TimeField'
import {
  INCIDENT_LOCATIONS,
  INCIDENT_STATUSES,
  INCIDENT_TYPES,
} from '../incidentConstants'

const SectionHeader = ({ title, caption }) => (
  <Box>
    <Typography variant="h6" fontWeight={700}>
      {title}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    )}
  </Box>
)

// Older incidents may hold option values that predate the shared constant
// lists — keep them selectable so the Select doesn't render blank.
const withLegacyValue = (options, value) =>
  value && !options.includes(value) ? [value, ...options] : options

const Details = ({ incident, handleChange }) => {
  const [showFeedback, setShowFeedback] = useState(null)

  const setDatePart = (newDate) => {
    if (!newDate?.isValid?.()) return
    const current = incident.incidentDate ? moment(incident.incidentDate) : moment()
    handleChange(
      'incidentDate',
      current
        .clone()
        .year(newDate.year())
        .month(newDate.month())
        .date(newDate.date())
        .toISOString(),
    )
  }

  const setTimePart = (newTime) => {
    if (!newTime?.isValid?.()) return
    const current = incident.incidentDate ? moment(incident.incidentDate) : moment()
    handleChange(
      'incidentDate',
      current
        .clone()
        .hour(newTime.hour())
        .minute(newTime.minute())
        .toISOString(),
    )
  }

  return (
    <Stack gap={2}>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Stack gap={2}>
            <SectionHeader
              title="Incident Details"
              caption="Corrections made during review are recorded in the audit history."
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              <AnDateField
                date={incident?.incidentDate}
                onChange={setDatePart}
                label="Incident Date"
                fullWidth
                size="small"
              />
              <AnTimeField
                time={incident?.incidentDate}
                onChange={setTimePart}
                label="Incident Time"
                fullWidth
                size="small"
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              <TextField
                select
                label="Incident Type"
                value={incident?.incidentType ?? ''}
                onChange={(e) => handleChange('incidentType', e.target.value)}
                size="small"
                fullWidth
              >
                {withLegacyValue(INCIDENT_TYPES, incident?.incidentType).map(
                  (type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ),
                )}
              </TextField>
              <TextField
                select
                label="Incident Location"
                value={incident?.incidentLocation ?? ''}
                onChange={(e) => handleChange('incidentLocation', e.target.value)}
                size="small"
                fullWidth
              >
                {withLegacyValue(
                  INCIDENT_LOCATIONS,
                  incident?.incidentLocation,
                ).map((location) => (
                  <MenuItem key={location} value={location}>
                    {location}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              label="Description of Incident"
              value={incident?.description ?? ''}
              onChange={(e) => handleChange('description', e.target.value)}
              multiline
              minRows={4}
              fullWidth
              size="small"
            />
            <TextField
              label="Initial Actions Taken"
              value={incident?.initialActions ?? ''}
              onChange={(e) => handleChange('initialActions', e.target.value)}
              multiline
              minRows={2}
              fullWidth
              size="small"
            />
          </Stack>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Stack gap={2}>
            <SectionHeader
              title="Review"
              caption="Status, ownership and visibility."
            />
            <TextField
              select
              label="Status"
              value={incident?.status ?? ''}
              onChange={(e) => handleChange('status', e.target.value)}
              size="small"
              fullWidth
            >
              {INCIDENT_STATUSES.filter(
                (status) => status !== 'New' || incident?.status === 'New',
              ).map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
            <StaffSelector
              value={incident?.responsibleManager ?? null}
              fullWidth
              size="small"
              label="Assigned To"
              onChange={(e, value) => handleChange('responsibleManager', value)}
            />
            <StaffSelector
              value={incident?.hiddenFrom ?? []}
              multiple
              fullWidth
              size="small"
              label="Hidden From"
              onChange={(e, value) => handleChange('hiddenFrom', value)}
              preventSelectingSelf
            />
            {incident?.linkedFeedback && (
              <>
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{ cursor: 'pointer', borderRadius: 2 }}
                  onClick={() => setShowFeedback(incident.linkedFeedback)}
                >
                  This incident is linked to Patient Feedback — click to view.
                </Alert>
                <FeedbackDrawer
                  selectedFeedback={showFeedback}
                  setSelectedFeedback={setShowFeedback}
                  isIncidentView
                />
              </>
            )}
          </Stack>
        </Grid>
      </Grid>
      <Divider />
      <Attachments
        existingAttachments={incident.files}
        s3folder="governance/incidents"
        actionAfterUpload={(s3key) => {
          API.put('/incident/' + incident._id, {
            $push: {
              files: s3key,
            },
          })
        }}
      />
    </Stack>
  )
}

export default Details
